import { createContext, FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { passengersCountBookingSelector } from "@ryde/shared";

import {
  BookingCalendarStyled,
  BookingCalendarWrapper,
  NoCalendarBookingDataStyled,
} from "./parts";

export interface IBookingCalendarContext {
  bookingCalendarHeight: number;
  bookingCalendarWidth: number;
}

export const BookingCalendarContext = createContext<IBookingCalendarContext>({
  bookingCalendarHeight: 0,
  bookingCalendarWidth: 0,
});

export const BookingsCalendar: FC = () => {
  const passengersCount = useRecoilValue(passengersCountBookingSelector);
  const { t } = useTranslation();
  const [
    { bookingCalendarHeight, bookingCalendarWidth },
    setBookingCalendarContextValue,
  ] = useState({
    bookingCalendarHeight: 0,
    bookingCalendarWidth: 0,
  });
  const bookingCalendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = bookingCalendarRef.current;
    if (!node) {
      return;
    }

    const resizeHandler = (): void => {
      const { width, height } = node.getBoundingClientRect();
      setBookingCalendarContextValue({
        bookingCalendarHeight: height,
        bookingCalendarWidth: width,
      });
    };

    resizeHandler();
    const resizeObserver = new ResizeObserver(resizeHandler);
    resizeObserver.observe(node);
    window.addEventListener("resize", resizeHandler);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  return (
    <BookingCalendarStyled ref={bookingCalendarRef}>
      <BookingCalendarContext.Provider
        value={{ bookingCalendarHeight, bookingCalendarWidth }}
      >
        {!!passengersCount && <BookingCalendarWrapper />}
        {!passengersCount && (
          <NoCalendarBookingDataStyled>
            {t("ui.NO_CALENDAR_DATA")}
          </NoCalendarBookingDataStyled>
        )}
      </BookingCalendarContext.Provider>
    </BookingCalendarStyled>
  );
};
